import React, { useRef } from "react";
import { List, ListItem, Region, Section, TableCell } from "../core/pdf/types";
import { useDesigner } from "./useDesigner";
import { ElementContext, useElementContext } from "./useElementContext";
import { useContentState } from "./useContentState";
import { closest, closestKey } from "../core/utils/dom";
import { HeaderFooterEditor } from "./HeaderFooterEditor";
import { ContentItems } from "./ContentItems";
import { toMillimeters, toPdfPoint } from "../core/pdf/utils";
import { broadcast } from "../core/hooks/useChannel";
import { DesignerMessages } from "./types";

export function SectionEditor(props: {
	section: Section,
}) {

	const designer = useDesigner();

	const section = props.section;

	let style: any;
	if (designer.structure.layoutType === "static") {
		style = {};
		if (section.width && section.height) {
			style.width = toMillimeters(section.width) + "mm";
			style.height = toMillimeters(section.height) + "mm";
			style.position = "relative";
		}
	
	} else {
		// dynamic
		style = designer.createStyle(designer.structure.defaults, section);

	}

	const elementContext = useElementContext(section);

	const state = useContentState(elementContext, React.useCallback((msg: any) => {

	}, []));

	const header = section.header;
	const footer = section.footer;
	if (header) {
		if (!header.id) header.id = designer.nextID();
		if (!header.type) header.type = "header";
	}
	if (footer) {
		if (!footer.id) footer.id = designer.nextID();
		if (!footer.type) footer.type = "footer";
	}

	return (
		<ElementContext.Provider value={elementContext}>
			<div
				className={"section content-element " + state.classes}
				data-key={section.id}
				id={state.elementID}

				style={style}

				onClick={(e) => {
					const key = closestKey(e.target, ".content-element");
					if (key) {
						const ref = designer.getRef(Number(key));
						if (ref) {
							designer.setFocus(ref);
						}
					// } else {
					// 	// clicked out of area, so remove focus entirely
					// 	if (designer.focus) designer.setFocus(undefined);
					}
				}}

				onKeyDown={(e) => {
					if (designer.focus) {
						if (e.code === "Enter") {							
							const focusedContext = designer.getContext(designer.focus.id!);
							if (focusedContext) {
								let handled = false;

								const parent = focusedContext.parent.def as any;
								if (parent.content) {
									const p = designer.createItem("p");
									const i = parent.content.indexOf(focusedContext.def);
									parent.content.splice(i, 0, p);
									designer.setFocus(p);
	
									handled = true;
								} else {
									if (focusedContext.def.type === "listItem") {
										const list = parent as List;
										const li = focusedContext.def as ListItem;
										const i = list.items.indexOf(li);
										const liNew = designer.createItem("listItem") as ListItem;
										list.items.splice(i, 0, liNew);
										designer.setFocus(liNew.content[0]);

										handled = true;

									} else if (focusedContext.def.type === "tableCell") {
										// insert para at start of cell content
										const p = designer.createItem("p");
										(focusedContext.def as TableCell).content.splice(0, 0, p!);
										designer.setFocus(p);
										handled = true;


									}

								}

								if (handled) {
									designer.refreshItem(parent);	
									e.preventDefault();	
								}
							}
				
						} else if (e.code === "Backspace") {
							if (designer.focus.type === "hr") {
								// convert back to standard paragraph
								const focusedContext = designer.getContext(designer.focus.id!);
								if (focusedContext) {
									const p = designer.createItem("p");
									const parent = focusedContext.parent.def as any;
									const i = parent.content.indexOf(focusedContext.def);
									parent.content.splice(i, 1, p);
									designer.setFocus(p);
									designer.refreshItem(parent);	
									e.preventDefault();
								}	

							} else if (designer.focus.type === "listItem") {
								// delete this list item
								const focusedContext = designer.getContext(designer.focus.id!);
								if (focusedContext) {
									const parent = focusedContext.parent.def as any;
									const list = parent as List;
									const li = focusedContext.def as ListItem;
									const i = list.items.indexOf(li);
									list.items.splice(i, 1);
									designer.setFocus((list.items.length > i) ? list.items[i].content[0] : undefined);
									designer.refreshItem(parent);
									e.preventDefault();
								}
							}

						}
					}
				}}

			>
				{
					(designer.bgPdfPages && section.pdfPageIndex !== undefined) && 
					<EmbedPdf
						pdfPageIndex={section.pdfPageIndex}
						width={section.width!}
						height={section.height!}
					/>
				}

				{ section.header && <HeaderFooterEditor type="header" headerFooter={section.header} /> }

					<ContentItems
						content={section.content}
					/>

				{ section.footer && <HeaderFooterEditor type="footer" headerFooter={section.footer} /> }

				<RegionSelector sectionID={state.elementID} />
			</div>
		</ElementContext.Provider>
	);
}

export function EmbedPdf(props: {
	pdfPageIndex: number,
	width: number,
	height: number,
}) {

	const designer = useDesigner();

	const page = designer.bgPdfPages[props.pdfPageIndex];

	const ref = React.useRef<any>();

	React.useLayoutEffect(() => {
		// NOTE: higher scale value the better quality
		const viewport = page.getViewport({ scale: 2 });

		const canvas = ref.current;
		if (!canvas) return;

		var context = canvas.getContext('2d');
		canvas.height = viewport.height;
		canvas.width = viewport.width;

		// render pdf page into canvas context
		const renderContext = {
			canvasContext: context,
			viewport: viewport,
		};

		var renderTask = page.render(renderContext);
		renderTask.promise.then(function () {
			// finished
		});

	}, [page]);

	return (
		<canvas
			ref={ref}
			style={{
				width: toMillimeters(props.width) + "mm", 
				height: toMillimeters(props.height) + "mm",
			}}
		></canvas>
	);
}


interface RegionSelectorState {
	scrollElement: any,

	downAt?: {
		x: number,
		y: number,
	}
	at: {
		x: number,
		y: number,
	},
	scale: {
		x: number,
		y: number,
	}
}

export function RegionSelector(props: {
	sectionID: string,
}) {

	const designer = useDesigner();

	const context = useElementContext({});

	React.useLayoutEffect(() => {
		const sect = document.getElementById(props.sectionID);
		if (!sect) return;

		const divWithScroll = closest(sect, ".pdf-designer");
		state.scrollElement = divWithScroll;

		const def = context.parent.def as Section;

		state.scale.x = sect.clientWidth / toMillimeters(def.width!);
		state.scale.y = sect.clientHeight / toMillimeters(def.height!);

		sect.addEventListener("mousedown", mousedown);
		sect.addEventListener("mousemove", mousemove);
		sect.addEventListener("mouseup", mouseup);
		sect.addEventListener("contextmenu", contextmenu);

		return () => {
			sect.removeEventListener("mousedown", mousedown);
			sect.removeEventListener("mousemove", mousemove);
			sect.removeEventListener("mouseup", mouseup);
			sect.removeEventListener("contextmenu", contextmenu);
		};
	}, []);

	const stateRef = useRef<RegionSelectorState>({
		at: { x: 0, y: 0 },
		scale: {
			x: 0,
			y: 0,
		},
		scrollElement: {},
	})
	const state = stateRef.current;

	function contextmenu(e: any) {
		e.preventDefault();
		return false;
	}

	function mousedown(e: any) {
		if (e.button === 2) {
			state.downAt = getPoint(e);
			e.preventDefault();
			return false;

		} else {
			if (e.ctrlKey) {
				const point = getPoint(e);
				point.x = toPdfPoint(point.x / state.scale.x);
				point.y = toPdfPoint(point.y / state.scale.y);
		
				broadcast(designer.channel, {
					type: DesignerMessages.ADD_REGION,
					point,
				});
			}

		}
	}
	function mouseup(e: any) {
		if (!state.downAt) return;

		// in mm
		const x = state.downAt.x / state.scale.x;
		const y = state.downAt.y / state.scale.y;
		const w = (state.at.x / state.scale.x) - x;
		const h = (state.at.y / state.scale.y) - y;
		if (w >= 5 && h >= 4) {
			const section = context.parent.def as Section;
			const region = designer.createItem("region") as Region;
			region.position = {
				x: toPdfPoint(x),
				y: toPdfPoint(y),
			};
			region.size = {
				w: toPdfPoint(w),
				h: toPdfPoint(h),
			};
			section.content.push(region);

			// refresh section
			designer.refreshItem(section);
		}



		state.downAt = undefined;
		refresh({});
	}
	function mousemove(e: any) {
		state.at = getPoint(e);

		//console.log(`x: ${Math.round(state.at.x / state.scale.x)}, y: ${Math.round(state.at.y / state.scale.y)}`);

		if (state.downAt) refresh({});
	}

	function getPoint(e: any) {
		const section = closest(e.target, ".section") as any;
		if (!section) {
			return {
				x: 0, y: 0,
			};
		}
		return {
			x: e.clientX - section.offsetLeft + state.scrollElement.scrollLeft,
			y: e.clientY - section.offsetTop + state.scrollElement.scrollTop,
		};
	}

	const [,refresh] = React.useState({});

	let region;

	if (state.downAt) {
		const x = state.downAt.x;
		const y = state.downAt.y;
		const w = state.at.x - x;
		const h = state.at.y - y;

		const style = {
			left: (x / state.scale.x) + "mm",
			top: (y / state.scale.y)  + "mm",
			width: (w / state.scale.x)  + "mm",
			height: (h / state.scale.y)  + "mm",
		};
		region = <div style={style} className="region"></div>
	}

	return (
		<>
			{region}
		</>
	);
}